/**
 * TaskNotificationProvider
 *
 * Bootstraps the service worker, requests OS notification permission,
 * and starts the task-polling loop once the user is authenticated.
 * Shows a greeting OS notification on each new login session.
 */

import { FC, ReactNode, useEffect, useRef } from 'react';
import { Button, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBell, IconBellOff } from '@tabler/icons-react';
import useAuthStore from '@/core/services/auth/auth';
import {
  registerServiceWorker,
  requestNotificationPermission,
  startTaskNotificationPolling,
  stopTaskNotificationPolling,
  clearNotificationState,
  hasNotificationPermission,
  sendGreetingNotification,
} from '@/core/services/notifications/task-notification.service';

const TASK_DOC_TYPE = 'TSK';
const PERMISSION_PROMPT_DELAY_MS = 3000;

interface TaskNotificationProviderProps {
  children: ReactNode;
}

export const TaskNotificationProvider: FC<TaskNotificationProviderProps> = ({ children }) => {
  const { authState } = useAuthStore();
  const user = authState?.user;
  const swRegistered = useRef(false);
  const pollingStarted = useRef(false);

  // ── Register SW once on mount ──────────────────────────────────────────────
  useEffect(() => {
    if (swRegistered.current) return;
    swRegistered.current = true;
    registerServiceWorker();
  }, []);

  // ── Start/stop polling based on auth state ─────────────────────────────────
  useEffect(() => {
    if (!user?.docNo) {
      if (pollingStarted.current) {
        stopTaskNotificationPolling();
        clearNotificationState();
        pollingStarted.current = false;
      }
      return;
    }

    const firstName = user.firstName || user.userName || '';

    const initNotifications = async () => {
      // Already granted — start polling + send greeting on every login
      if (hasNotificationPermission()) {
        // Always send greeting — clearNotificationState() resets SS_GREETING_SHOWN on logout
        sendGreetingNotification(firstName);
        if (!pollingStarted.current) {
          console.log('[Notifications] Permission already granted. Starting polling.');
          startTaskNotificationPolling(user.docNo, TASK_DOC_TYPE);
          pollingStarted.current = true;
        }
        return;
      }

      // Not yet asked — show a friendly toast prompt
      if ('Notification' in window && Notification.permission === 'default') {
        console.log('[Notifications] Permission not yet granted. Showing prompt toast in', PERMISSION_PROMPT_DELAY_MS, 'ms.');
        setTimeout(() => {
          notifications.show({
            id: 'notif-permission-prompt',
            title: 'Stay on top of your tasks',
            message: (
              <Group gap="sm" mt={4}>
                <Text size="xs" c="dimmed">
                  Enable notifications to get alerted when tasks are assigned or overdue.
                </Text>
                <Button
                  size="xs"
                  leftSection={<IconBell size={14} />}
                  onClick={async () => {
                    notifications.hide('notif-permission-prompt');
                    const perm = await requestNotificationPermission();
                    if (perm === 'granted') {
                      startTaskNotificationPolling(user.docNo, TASK_DOC_TYPE);
                      pollingStarted.current = true;
                      sendGreetingNotification(firstName);
                      notifications.show({
                        title: 'Notifications enabled',
                        message: "You'll be notified about task assignments and reminders.",
                        color: 'green',
                        autoClose: 3000,
                      });
                    }
                  }}
                >
                  Enable
                </Button>
                <Button
                  size="xs"
                  variant="subtle"
                  color="gray"
                  leftSection={<IconBellOff size={14} />}
                  onClick={() => notifications.hide('notif-permission-prompt')}
                >
                  Not now
                </Button>
              </Group>
            ),
            color: 'blue',
            autoClose: false,
            withCloseButton: true,
          });
        }, PERMISSION_PROMPT_DELAY_MS);
      } else {
        console.log('[Notifications] Permission denied by user. Notifications disabled.');
      }
    };

    initNotifications();

    return () => {
      stopTaskNotificationPolling();
    };
  }, [user?.docNo]); // eslint-disable-line react-hooks/exhaustive-deps

  return <>{children}</>;
};
